import { Avatar } from "@package/subframe/components/Avatar";
import { Button } from "@package/subframe/components/Button";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { FeatherArrowLeft, FeatherCheck, FeatherX } from "@subframe/core";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";
import { useVerifyEmailCommandControllerVerifyEmail } from "@/generated/auth/auth";

export const Route = createFileRoute("/_auth/verify-email")({
  validateSearch: (search: Record<string, unknown>): { oobCode?: string } => {
    return {
      oobCode: search.oobCode ? String(search.oobCode) : undefined,
    };
  },
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { oobCode } = useSearch({ from: "/_auth/verify-email" });
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<
    "pending" | "success" | "error"
  >("pending");

  const { mutate: verifyEmail } = useVerifyEmailCommandControllerVerifyEmail({
    mutation: {
      onSuccess: () => {
        setVerificationStatus("success");
        toast.success("Email verified successfully!");
        setTimeout(() => {
          navigate({ to: "/login" });
        }, 2000);
      },
      onError: (error: unknown) => {
        setVerificationStatus("error");
        let errorMessage =
          "Failed to verify email. The token may be invalid or expired.";

        if (error && typeof error === "object") {
          if (
            "response" in error &&
            error.response &&
            typeof error.response === "object" &&
            "data" in error.response
          ) {
            const responseData = error.response.data as Record<string, unknown>;
            if (
              responseData.message &&
              typeof responseData.message === "string"
            ) {
              errorMessage = responseData.message;
            }
          } else if ("message" in error && typeof error.message === "string") {
            errorMessage = error.message;
          }
        }

        toast.error(errorMessage);
      },
    },
  });

  useEffect(() => {
    if (oobCode) {
      verifyEmail({ data: { code: oobCode } });
    }
  }, [oobCode, verifyEmail]);

  // Invalid or missing token
  if (!oobCode) {
    return (
      <div className="flex w-full items-center justify-center h-screen">
        {/* Left side - Testimonial */}
        <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
          <Avatar size="x-large" image="/images/testimonial-avatar.png">
            SK
          </Avatar>
          <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
            <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
              &quot;This platform has transformed how we manage compliance at
              our nursery. The interface is intuitive and saves us countless
              hours every week.&quot;
            </span>
            <div className="flex flex-wrap items-end gap-2">
              <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
                Sarah Kennedy
              </span>
              <span className="text-body font-body text-primary-rose">–</span>
              <span className="text-body font-body text-primary-rose">
                Director, Little Sprouts Nursery
              </span>
            </div>
          </div>
        </div>

        {/* Right side - Error */}
        <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
          <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
            <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
              <img
                className="h-8 flex-none object-cover"
                src="/images/logo.svg"
                alt="Logo"
              />
            </div>

            <span className="text-heading-1 font-heading-1 text-neutral-900">
              Invalid verification link
            </span>

            <span className="text-body font-body text-subtext-color">
              The email verification link is invalid or has expired.
            </span>

            <Link to="/login" className="w-full">
              <Button
                className="h-12 w-full flex-none"
                variant="brand-primary"
                size="large"
                icon={<FeatherArrowLeft />}
              >
                Back to sign in
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full items-center justify-center h-screen">
      {/* Left side - Testimonial */}
      <div className="flex flex-col items-start justify-end gap-6 self-stretch bg-primary-navy-800 pl-24 pr-12 pt-12 pb-32 w-1/2 mobile:hidden">
        <Avatar size="x-large" image="/images/testimonial-avatar.png">
          SK
        </Avatar>
        <div className="flex w-full flex-col items-start gap-6 max-w-[80%]">
          <span className="font-['Manrope'] text-[36px] font-[500] leading-[40px] tracking-wide text-white">
            &quot;This platform has transformed how we manage compliance at our
            nursery. The interface is intuitive and saves us countless hours
            every week.&quot;
          </span>
          <div className="flex flex-wrap items-end gap-2">
            <span className="font-['Manrope'] text-[20px] font-[700] leading-[24px] tracking-tight text-primary-rose">
              Sarah Kennedy
            </span>
            <span className="text-body font-body text-primary-rose">–</span>
            <span className="text-body font-body text-primary-rose">
              Director, Little Sprouts Nursery
            </span>
          </div>
        </div>
      </div>

      {/* Right side - Content */}
      <div className="flex flex-col items-center justify-center self-stretch bg-white px-12 py-12 relative w-1/2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0 mobile:self-stretch mobile:px-6 mobile:py-6">
        <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
          {/* Logo */}
          <div className="flex items-center gap-2 mobile:absolute mobile:left-8 mobile:top-8">
            <img
              className="h-8 flex-none object-cover"
              src="/images/logo.svg"
              alt="Logo"
            />
          </div>

          {verificationStatus === "pending" && (
            <>
              <span className="text-heading-1 font-heading-1 text-neutral-900">
                Verifying email
              </span>
              <span className="text-body font-body text-subtext-color">
                Please wait while we verify your email address...
              </span>
              <div className="flex w-full items-center justify-center py-8">
                <Spinner className="h-8 w-8" />
              </div>
            </>
          )}

          {verificationStatus === "success" && (
            <>
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success-100">
                <FeatherCheck className="h-8 w-8 text-success-600" />
              </div>
              <span className="text-heading-1 font-heading-1 text-neutral-900">
                Email verified!
              </span>
              <span className="text-body font-body text-subtext-color">
                Your email has been successfully verified. Redirecting to
                login...
              </span>
              <div className="flex w-full flex-col items-start gap-2 p-4 bg-success-50 rounded-lg">
                <span className="text-body font-body text-success-700">
                  You can now sign in to your account with your credentials.
                </span>
              </div>
              <Link to="/login" className="w-full">
                <Button
                  className="h-12 w-full flex-none"
                  variant="brand-primary"
                  size="large"
                >
                  Continue to sign in
                </Button>
              </Link>
            </>
          )}

          {verificationStatus === "error" && (
            <>
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error-100">
                <FeatherX className="h-8 w-8 text-error-600" />
              </div>
              <span className="text-heading-1 font-heading-1 text-neutral-900">
                Verification failed
              </span>
              <span className="text-body font-body text-subtext-color">
                We couldn&apos;t verify your email. The link may be invalid or
                expired.
              </span>
              <div className="flex w-full flex-col items-start gap-2 p-4 bg-error-50 rounded-lg">
                <span className="text-body font-body text-error-700">
                  This could happen if the verification link has expired or has
                  already been used.
                </span>
              </div>
              <Link to="/login" className="w-full">
                <Button
                  className="h-12 w-full flex-none"
                  variant="brand-primary"
                  size="large"
                >
                  Go to login
                </Button>
              </Link>
              <div className="flex w-full items-center justify-center">
                <Link to="/">
                  <LinkButton variant="brand" size="small">
                    Return to home
                  </LinkButton>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
